import React from 'react';
import { Fragment } from 'react';
import {
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
} from 'react-bootstrap';
import Button from '../Button';
import ModalHeader from '../ModalHeader';
import Tabs from '../Tabs/Tabs';
import AreaMixedChart from './AreaMixedChart';
import DonutChart from './DonutChart';
import ColumnsChart from './ColumnsChart';
import MultipleColumnsChart from './MultipleColumnsChart';
import DoubleComparisonColumnsChart from './DoubleComparisonColumnsChart';
import DonutCustomLegend from './CustomLegends/DonutCustomLegend';
import ColumnsCustomLegend from './CustomLegends/ColumnsCustomLegend';
import useModal from '../../hooks/useModal';
import { defaultSliderInitialInputs, fixedAssumptionsData } from '../../services/retirementPlanning';
import { generateIntegerRange } from '../../utils/utils';

const areaMixedChartDemoData = {
  ageRange: generateIntegerRange(fixedAssumptionsData.cAge, 100)
}

const tabsData = [
  {
    name: 'Health and Welfare',
    content: (
      <div className="pt-11">
        <h6>Health</h6>
        <MultipleColumnsChart
          cAge={fixedAssumptionsData.cAge}
          ageRange={areaMixedChartDemoData.ageRange}
          iEECont={defaultSliderInitialInputs.iEECont}
          iROR={defaultSliderInitialInputs.iROR}
          iRetAge={defaultSliderInitialInputs.iRetAge}
          cHSA={fixedAssumptionsData.cHSA}
          cPS={fixedAssumptionsData.cPS}
          cPay={fixedAssumptionsData.cPay}
          c401k={fixedAssumptionsData.c401k}
          SScal={fixedAssumptionsData.SScal}
          iIncRep={defaultSliderInitialInputs.iIncRep}
          Inf={fixedAssumptionsData.Inf}
        />
      </div>
    ),
  },

  {
    name: 'Retirement',
    content: (
      <div className="pt-11">
        <h6>Retirement</h6>
        <AreaMixedChart
          cAge={fixedAssumptionsData.cAge}
          ageRange={areaMixedChartDemoData.ageRange}
          iEECont={defaultSliderInitialInputs.iEECont}
          iROR={defaultSliderInitialInputs.iROR}
          iRetAge={defaultSliderInitialInputs.iRetAge}
          cHSA={fixedAssumptionsData.cHSA}
          cPS={fixedAssumptionsData.cPS}
          cPay={fixedAssumptionsData.cPay}
          c401k={fixedAssumptionsData.c401k}
          SScal={fixedAssumptionsData.SScal}
          iIncRep={defaultSliderInitialInputs.iIncRep}
          Inf={fixedAssumptionsData.Inf}
        />
      </div>
    ),
  },
];

const donutChartData = {
  // The "grid padding - left" setting is a hack to reduce the huge margin to the left that donut renders.
  // See: https://github.com/apexcharts/apexcharts.js/issues/1272
  grid: {
    padding: {
      left: -20,
    },
  },
  legend: {
    show: false,
  },

  dataLabels: {
    enabled: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 200,
        },
        grid: {
          padding: {
            left: 0,
          },
        },
        legend: {
          show: false,
        },
        title: {},
      },
    },
  ],
  colors: ['#074CBC', '#F48462', '#07B4A0', '#EEA968', '#92E3A9'],
  series: [95072, 15905, 14791, 6500, 0],
  labels: [
    'Compensation',
    'Health and Welfare',
    'Retirement',
    'Equity',
    'Other Benefits',
  ],
  tooltip: {
    enabled: false,
  },

  // DISPLAY TEXT IN DONUT CENTRE:
  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          name: {
            offsetY: 10,
          },
          total: {
            show: true,
            showAlways: true,
            fontSize: '18px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 500,
            label: `$132,268`,
            formatter: function (a: any) {
              return '';
            },
          },
        },
      },
    },
  },
};

const columnChartData = {
  chart: {
    stacked: true,
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  colors: [
    '#C4D2D9',
    '#4381A5',
    '#2A556D',
    '#F83966',
    '#F48462',
    '#F17AD0',
    '#0C9C8E',
  ],
  series: [
    {
      name: 'Medical',
      data: [7000, 3500],
    },
    {
      name: 'Nicotine-Free Discount',
      data: [0, -200],
    },
    {
      name: 'Dental',
      data: [3000, 200],
    },
    {
      name: 'Vision',
      data: [3650, 250],
    },
    {
      name: 'Health Care Flexible Spending Acount',
      data: [0, 1500],
    },
    {
      name: 'Basic Employee Life and AD&D',
      data: [2255, 0],
    },
    {
      name: 'Supplemental Employee Life and AD&D',
      data: [0, 4400],
    },
  ],

  dataLabels: {
    enabled: false,
  },
  fill: {
    opacity: 1,
  },
  legend: {
    show: false,
  },
  plotOptions: {
    bar: {
      horizontal: false,
    },
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: {
          position: 'bottom',
          offsetX: -10,
          offsetY: 0,
        },
      },
    },
  ],
  tooltip: {
    enabled: false,
  },
  xaxis: {
    overwriteCategories: ['You Contribute', 'We Contribute'],
  },
  yaxis: {
    title: {
      text: '',
    },
    min: -2500,
    max: 17500,
    tickAmount: 8,

    labels: {
      formatter: function (a: any) {
        return '$' + a;
      },
    },
    categories: [
      '-2,500',
      '0',
      '2,500',
      '7,500',
      '10,000',
      '12,500',
      '15,000',
      '17,500',
    ],
  },
};

const doubleComparisonChartOptions = {
  chart: {
    id: 'comparison-columns',
    height: 350,
    stacked: true,
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  colors: ['#07B4A0', '#EEA968', '#0795B4'],
  series:[
    {
      name: '401 (k) Plan',
      data: [
        { x: 2, y: 4500 }, //2 is 'We Year-to-Date'
        { x: 4, y: 4334 }, //4 is 'You Year-to-Date'
        { x: 8, y: 4500 }, //8 is 'We Annual Projection'
        { x: 10, y: 17334 }, //10 is 'You Annual Projection'
      ],
    },
    {
      name: 'Pension Plan',
      data: [
        { x: 2, y: 4345 },
        { x: 4, y: 0 },
        { x: 8, y: 4345 },
        { x: 10, y: 0 },
      ],
    },
    {
      name: 'Estimated Social Security and Medicare',
      data: [
        { x: 2, y: 5946 },
        { x: 4, y: 6469 },
        { x: 8, y: 14866 },
        { x: 10, y: 16173 },
      ],
    },
  ],
  dataLabels: {
    enabled: false,
  },
  fill: {
    opacity: 1,
  },
  legend: {
    show: false,
  },
  tooltip: {
    enabled: false,
  },
  xaxis: {
    labels: {
      show: false,
    },
  },
  yaxis: {
    categories: [0, 2500, 7500, 10000, 12500, 15000, 17500],
    title: {
      text: '',
    },
    labels: {
      formatter: function (a: any) {
        return '$' + a;
      },
    },
  },
};

function ApexChartDemo() {
  const [showModal, handleShow, handleClose] = useModal();

  return (
    <Fragment>
      <Container>
        <h4>Apex charts demo</h4>
        <section className="mt-4 mb-10 col-3">
          <DonutChart series={donutChartData.series} options={donutChartData} />
          <DonutCustomLegend
            options={donutChartData}
          />
        </section>
        <section className="mt-4 mb-10 col-lg-6 col-12">
          <ColumnsChart
            series={columnChartData.series}
            options={columnChartData}
          />
          <ColumnsCustomLegend
            series={columnChartData.series}
            colors={columnChartData.colors}
            total={['$15,905', '$9,650']}
          />
        </section>
        <section className="mt-4 mb-10 col-lg-6 col-12">
          <AreaMixedChart
            cAge={fixedAssumptionsData.cAge}
            ageRange={areaMixedChartDemoData.ageRange}
            iEECont={defaultSliderInitialInputs.iEECont}
            iROR={defaultSliderInitialInputs.iROR}
            iRetAge={defaultSliderInitialInputs.iRetAge}
            cHSA={fixedAssumptionsData.cHSA}
            cPS={fixedAssumptionsData.cPS}
            cPay={fixedAssumptionsData.cPay}
            c401k={fixedAssumptionsData.c401k}
            SScal={fixedAssumptionsData.SScal}
            iIncRep={defaultSliderInitialInputs.iIncRep}
            Inf={fixedAssumptionsData.Inf}
          />
        </section>
        <section className="mt-4 mb-10 col-lg-6 col-12">
          <MultipleColumnsChart
            cAge={fixedAssumptionsData.cAge}
            ageRange={areaMixedChartDemoData.ageRange}
            iEECont={defaultSliderInitialInputs.iEECont}
            iROR={defaultSliderInitialInputs.iROR}
            iRetAge={defaultSliderInitialInputs.iRetAge}
            cHSA={fixedAssumptionsData.cHSA}
            cPS={fixedAssumptionsData.cPS}
            cPay={fixedAssumptionsData.cPay}
            c401k={fixedAssumptionsData.c401k}
            SScal={fixedAssumptionsData.SScal}
            iIncRep={defaultSliderInitialInputs.iIncRep}
            Inf={fixedAssumptionsData.Inf}
          />
        </section>
        <section className="mt-4 mb-10 col-lg-6 col-12">
          <DoubleComparisonColumnsChart options={doubleComparisonChartOptions}/>
        </section>

        <section>
          <h4> Apex Charts in modal (and in tabs)</h4>
          <Button variant="primary" onMouseDown={handleShow}>
            Charts in modal
          </Button>
          <Modal show={showModal} onHide={handleClose} size={'xl'}>
            <ModalHeader closeButton={true}>
              <h5 className="text-truncate">Company's Contribution</h5>
            </ModalHeader>
            <ModalBody>
              <Row>
                <Col>
                  <DonutChart
                    series={donutChartData.series}
                    options={donutChartData}
                  />
                </Col>
                <Col>
                  <DonutCustomLegend
                    options={donutChartData}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Container>
                    <Container className="mt-10">
                      <h6>Charts in Tabs component:</h6>
                      <Row>
                        <Tabs tabs={tabsData} />
                      </Row>
                    </Container>
                  </Container>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button variant="primary-outline" onMouseDown={handleClose}>
                Close
              </Button>
            </ModalFooter>
          </Modal>
        </section>
      </Container>
    </Fragment>
  );
}
export default ApexChartDemo;
